import type { ComponentPropsWithRef, ReactNode } from "react";
import { cx } from "../../cx";
import { buttonVariantClasses, type ButtonVariant } from "../Button/Button";

export interface IconButtonProps extends Omit<
  ComponentPropsWithRef<"button">,
  "children" | "aria-label" | "title"
> {
  /** Required: accessible name and tooltip. */
  readonly label: string;
  readonly icon: ReactNode;
  readonly variant?: ButtonVariant;
  /** Count bubble (e.g. active filters); hidden at 0. */
  readonly badge?: number;
  readonly pressed?: boolean;
}

/**
 * Icon-only button: 32 px visual, 44 px hit area on coarse pointers and below `md` (an
 * invisible `::before` extends the target without changing the layout box).
 */
export function IconButton({
  label,
  icon,
  variant = "secondary",
  badge,
  pressed,
  type = "button",
  className,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      data-icon-button=""
      className={cx(
        "relative inline-flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors select-none",
        "before:absolute before:-inset-1.5 before:content-[''] pointer-coarse:before:-inset-2.5 pointer-coarse:min-h-11 pointer-coarse:min-w-11 max-md:min-h-11 max-md:min-w-11 max-md:before:-inset-2",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
        "disabled:cursor-not-allowed disabled:opacity-60",
        variant === "secondary"
          ? "bg-surface-sunken text-on-action-secondary hover:bg-line"
          : buttonVariantClasses[variant],
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true" className="inline-flex">
        {icon}
      </span>
      {badge !== undefined && badge > 0 && (
        <span
          data-testid="icon-button-badge"
          className="absolute -end-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-action-primary px-1 text-[0.625rem] leading-4 font-semibold text-on-action-primary"
        >
          {badge}
        </span>
      )}
    </button>
  );
}
